import React, { useState, useEffect, useCallback } from "react";
import "./work_report.css";
import { toast } from "react-toastify";
import { addReport, getAllAssignedProjects } from "../../service/project_service";
import { useLocation } from "react-router-dom";

const AddWorkReport = () => {
  const location = useLocation();
  const { projectid, moduleid } = location.state || {};

  const [formData, setFormData] = useState({
    projectid: projectid || "",
    moduleid: moduleid || "",
    workdate: new Date().toISOString().split("T")[0],
    starttime: "",
    endtime: "",
    workinghours: "",
    taskname: "",
  });

  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await getAllAssignedProjects();
      if (response?.status === "success") {
        setProjects(response.data || []);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to fetch projects");
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Update modules when project changes
  useEffect(() => {
    if (formData.projectid && projects.length > 0) {
      const selectedProject = projects.find(
        (p) => Number(p.projectid) === Number(formData.projectid),
      );
      const projectModules = selectedProject?.modules || [];
      setModules(projectModules);

      // Only reset the moduleid if it's already set AND NOT in the new list of modules
      if (
        formData.moduleid &&
        projectModules.length > 0 &&
        !projectModules.some(
          (m) => Number(m.moduleid) === Number(formData.moduleid),
        )
      ) {
        setFormData((prev) => ({ ...prev, moduleid: "" }));
      }
    } else if (projects.length > 0) {
      // Only clear if projects list has loaded but projectid is null
      setModules([]);
      setFormData((prev) => ({ ...prev, moduleid: "" }));
    }
  }, [formData.projectid, projects, formData.moduleid]);

  // Calculate duration
  useEffect(() => {
    if (formData.starttime && formData.endtime) {
      const [startH, startM] = formData.starttime.split(":").map(Number);
      const [endH, endM] = formData.endtime.split(":").map(Number);

      const startTotalMins = startH * 60 + startM;
      const endTotalMins = endH * 60 + endM;

      if (endTotalMins > startTotalMins) {
        const diffMins = endTotalMins - startTotalMins;
        const h = Math.floor(diffMins / 60);
        const m = diffMins % 60;
        const formatted = `${h}.${m.toString().padStart(2, "0")}`;
        setFormData((prev) => ({
          ...prev,
          workinghours: formatted,
        }));
      } else {
        setFormData((prev) => ({ ...prev, workinghours: "" }));
      }
    }
  }, [formData.starttime, formData.endtime]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData({
      projectid: projectid || "",
      moduleid: moduleid || "",
      workdate: new Date().toISOString().split("T")[0],
      starttime: "",
      endtime: "",
      workinghours: "",
      taskname: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.projectid ||
      !formData.moduleid ||
      !formData.workdate ||
      !formData.taskname.trim()
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        projectid: Number(formData.projectid),
        moduleid: Number(formData.moduleid),
        workdate: formData.workdate,
        starttime: formData.starttime || "0.00",
        endtime: formData.endtime || "0.00",
        workinghours: formData.workinghours ? Number(formData.workinghours).toFixed(2) : "0.00",
        taskname: formData.taskname,
      };

      const response = await addReport(payload);
      if (response?.status === "success") {
        toast.success(response?.message || "Report added successfully");
        handleReset();
      } else {
        toast.error(response?.message || "Failed to add report");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-report-container">
      <h4>Add Work Report</h4>

      <div className="add-report-card">
        <form className="add-report-form" onSubmit={handleSubmit}>
          <div className="report-form-group">
            <label>Project *</label>
            <select
              name="projectid"
              value={formData.projectid}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Project</option>
              {projects.map((item) => (
                <option key={item.projectid} value={item.projectid}>
                  {item.projectname}
                </option>
              ))}
            </select>
          </div>

          <div className="report-form-group">
            <label>Project Module *</label>
            <select
              name="moduleid"
              value={formData.moduleid}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Module</option>
              {modules.map((item) => (
                <option key={item.moduleid} value={item.moduleid}>
                  {item.modulename}
                </option>
              ))}
            </select>
          </div>

          <div className="report-form-group">
            <label>Work Date *</label>
            <input
              type="date"
              name="workdate"
              value={formData.workdate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="report-form-group">
            <label>Start Time</label>
            <input
              type="time"
              name="starttime"
              value={formData.starttime}
              onChange={handleInputChange}
            />
          </div>

          <div className="report-form-group">
            <label>End Time</label>
            <input
              type="time"
              name="endtime"
              value={formData.endtime}
              onChange={handleInputChange}
            />
          </div>

          <div className="report-form-group">
            <label>Working Hours</label>
            <input
              type="text"
              name="workinghours"
              value={formData.workinghours}
              onChange={handleInputChange}
            />
          </div>

          <div className="report-form-group report-form-group-full">
            <label>Task Description *</label>
            <textarea
              name="taskname"
              value={formData.taskname}
              onChange={handleInputChange}
              rows="4"
              placeholder="Describe the work done..."
              required
            />
          </div>

          <div className="submit-buttons report-form-group-full">
            <button type="submit" className="apply-btn" disabled={loading}>
              {loading ? "Loading..." : "Submit"}
            </button>
            <button
              type="button"
              className="reset-btn"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
      <div style={{ height: "50px" }}></div>
    </div>
  );
};

export default AddWorkReport;
